import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { isValidPassword } from '../../utils/utils';
import * as SessionActions from '../../stores/actions/session';
import { APP, ONBOARDING_ROUTES } from '../../stores/session-status';
import classNames from 'classnames';
import { SessionState } from '../../stores/reducers/session';
import TextInput from '../general-components/text-input';
import LoadingSpinner from '../general-components/loading-spinner';

interface OwnProps {
	host?: boolean;
}

interface StateProps {
	SessionReducer: SessionState;
}

interface DispatchProps {
	updatePasswordGreenroom: Function;
	updatePasswordEvents: Function;
	setOnboardingRoute: Function;
}

const mapStateToProps = (state: any, ownProps: any) => {
	return { ...state, ...ownProps }
}

const mapDispatchToProps = (dispatch: Function) => {
	return {
		updatePasswordGreenroom: (password: string, code: string) => {
			dispatch(SessionActions.updatePasswordGreenroom(password, code));
		},
		updatePasswordEvents: (password: string, code: string) => {
			dispatch(SessionActions.updatePasswordEvents(password, code));
		},
		setOnboardingRoute: (route: number) => {
			dispatch(SessionActions.setOnboardingRoute(route))
		}
	}
}

type Props = StateProps & DispatchProps & OwnProps;

function PasswordReset(props: Props) {
	const [passwordValid, setPasswordValid] = useState(false);
	const [passwordMatch, setPasswordMatch] = useState(false);
	const [error, setError] = useState("");
	const [password, setPassword] = useState("");
	const [confirmedPassword, setConfirmedPassword] = useState("");

	//ui vars
	const [lengthReqSatistfied, setLengthReqSatistfied] = useState(true);
	const [numReqSatistfied, setNumReqSatistfied] = useState(true);
	const [lowerReqSatistfied, setLowerReqSatistfied] = useState(true);
	const [upperReqSatistfied, setUpperReqSatistfied] = useState(true);
	const [passMatch, setPassMatch] = useState(true);
	const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);

	const hash = useRef("");

	const {
		SessionReducer: {
			updatePasswordLoading,
			updatePasswordError,
			updatePasswordSuccess,
			selectedApp
		},
		updatePasswordGreenroom,
		updatePasswordEvents,
		setOnboardingRoute,
		host
	} = props;

	useEffect(() => {
		const pathParts = window.location.href.split("/");
		// Added the split "?" to get the last path paramter (hash). We also pass a query string param "app" for producers so we know which
		// app to sign the user in with on the backend after they reset their password (This is in App.tsx useEffect())
		if (pathParts[pathParts.length - 1].split("?")[0]) {
			hash.current = pathParts[pathParts.length - 1].split("?")[0];
		}

		// Remove everything from "password-reset" and on from the URL because after the password is reset
		// the producer will attempt to log in. When they log in we refresh the page and if "password-reset"
		// is still in the URL it goes back to that page.
		const url = new URL(window.location.href);
		window.history.replaceState(null, '', url.href.split("/password-reset")[0])
	}, [])

	function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		setHasSubmittedOnce(true);
		updateReqs(password, true);

		if (!passwordValid) return setError("Password does not fulfill all requirements.");
		if (!passwordMatch) return setError("Passwords do not match");
		setError("");

		if (selectedApp === APP.GREENROOM) {
			updatePasswordGreenroom(password, hash.current);
		} else {
			updatePasswordEvents(password, hash.current);
		}
	}

	function checkPassword(e: React.ChangeEvent<HTMLInputElement>) {
		setPassword(e.target.value);
		setPasswordValid(isValidPassword(e.target.value));
		updateReqs(e.target.value, hasSubmittedOnce);
	}

	function updateReqs(newPassword: string, canHighlight: boolean) {
		if (canHighlight) {
			setLengthReqSatistfied((newPassword.length >= 8) ? true : false);
			setLowerReqSatistfied((/[a-z]/.test(newPassword)) ? true : false);
			setUpperReqSatistfied((/[A-Z]/.test(newPassword)) ? true : false);
			setNumReqSatistfied((/[0-9]/.test(newPassword)) ? true : false);
			setPassMatch((newPassword === confirmedPassword) ? true : false);
		}
	}

	function checkConfirmedPassword(e: React.ChangeEvent<HTMLInputElement>) {
		setConfirmedPassword(e.target.value);
		setPasswordMatch(e.target.value === password);
	}

	function isFormValid() {
		return !!password;
	}

	function backToSignIn() {
		setOnboardingRoute(host ? ONBOARDING_ROUTES.PRODUCER_LOGIN : ONBOARDING_ROUTES.PRESENTER_LOGIN);
	}

	return (
		<div className={classNames("panel", !host ? "teal-green sign-in" : "producer sign-in")}>
			{!updatePasswordSuccess ? (
				<form onSubmit={submitForm} className="password-reset">
					<div className="presenter-message-container">
						<h2>Please enter a new password</h2>
					</div>

					<TextInput
						onChange={checkPassword}
						placeholder={"Password"}
						value={password}
						type={"password"}
						required
					/>

					<TextInput
						onChange={checkConfirmedPassword}
						placeholder={"Confirm Password"}
						value={confirmedPassword}
						type={"password"}
						required
					/>

					<span>Password Must Contain</span>
					<ul>
						<li className={lengthReqSatistfied ? "" : "red"}>At least 8 characters</li>
						<li className={numReqSatistfied ? "" : "red"}>At least 1 number</li>
						<li className={upperReqSatistfied ? "" : "red"}>At least 1 uppercase</li>
						<li className={lowerReqSatistfied ? "" : "red"}>At least 1 lowercase</li>
						<li className={passMatch ? "" : "red"}>Passwords must match</li>
					</ul>

					<button className="onboarding" disabled={!isFormValid()}>Reset Password{updatePasswordLoading && (
						<div style={{ width: 14, height: 14, marginLeft: 8 }}>
							<LoadingSpinner
								height={14}
								width={14}
								stroke={10}
							/>
						</div>
					)}</button>

					{updatePasswordError && (
						<div className="signin-error">
							<p>{updatePasswordError}</p>
						</div>
					)}

					<div className={classNames("signin-error", { show: !!error })}>
						<p>{error}</p>
					</div>
				</form>
			) : (
				<>
					<h2>Your password has successfully been reset.</h2>
					<div className="create-account">
						<span><span onClick={backToSignIn} className="clickable">Back to log in</span></span>
					</div>
				</>
			)}

		</div>
	)
}

export default connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(PasswordReset);